import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AllGroupedIndicatorDataDto, IndicatorValueDto, StateDto, DistrictDto, BlockDto, IndicatorFrequencyDto } from "@/lib/types/api";
import { handleApiError } from "@/lib/utils/errors";
import { showToast } from "@/lib/utils/toast";
import { apiClient } from "@/lib/api/client";
import { useAuth } from "@/lib/contexts/auth-context";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, X, BarChart3, Filter, Plus } from "lucide-react";
import { DataTableExpandable, ExpandableRowData, DetailColumn } from "@/components/datatable/DataTableExpandable";

export default function IndicatorData2Page() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [groupedData, setGroupedData] = useState<AllGroupedIndicatorDataDto[]>([]);
  const [expandedIndicators, setExpandedIndicators] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  
  // Add Indicator Data modal state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedLevelType, setSelectedLevelType] = useState<"district" | "block">("district");
  const [selectedDistrictForAdd, setSelectedDistrictForAdd] = useState<string>("");
  const [selectedBlockForAdd, setSelectedBlockForAdd] = useState<string>("");
  
  // Location type filter
  const [locationType, setLocationType] = useState<"both" | "district" | "block">("both");
  
  // Location filters
  const [districts, setDistricts] = useState<DistrictDto[]>([]);
  const [blocks, setBlocks] = useState<BlockDto[]>([]);
  const [selectedDistrict, setSelectedDistrict] = useState<string>("all");
  const [selectedBlock, setSelectedBlock] = useState<string>("all");
  
  // Other filters
  const [departments, setDepartments] = useState<{ id: string; name: string }[]>([]);
  const [frequencies, setFrequencies] = useState<IndicatorFrequencyDto[]>([]);
  const [frequencyFilter, setFrequencyFilter] = useState("all");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [programTypeFilter, setProgramTypeFilter] = useState("all");
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Check if user is regular User (not SuperAdmin)
  const isRegularUser = user && !user.roles.includes("SuperAdmin");
  const hasNoAssignment = isRegularUser && !user.districtId && !user.blockId;

  // Load all districts on mount (for district filter) - only for SuperAdmin
  useEffect(() => {
    if (isRegularUser) return; // Skip if user is regular User
    
    const loadAllDistricts = async () => {
      try {
        const result = await apiClient.getDistricts();
        if (result.success && result.data) {
          setDistricts(result.data);
        }
      } catch (err) {
        // Silently fail for non-critical filter data
        console.error('Failed to load districts:', err);
      }
    };
    loadAllDistricts();
  }, [isRegularUser]);

  // Load all blocks on mount (for block filter) - only for SuperAdmin
  useEffect(() => {
    if (isRegularUser) return; // Skip if user is regular User
    
    const loadAllBlocks = async () => {
      try {
        const result = await apiClient.getBlocks();
        if (result.success && result.data) {
          setBlocks(result.data);
        }
      } catch (err) {
        // Silently fail for non-critical filter data
        console.error('Failed to load blocks:', err);
      }
    };
    loadAllBlocks();
  }, [isRegularUser]);

  // Reset location filters when location type changes
  useEffect(() => {
    setSelectedDistrict("all");
    setSelectedBlock("all");
  }, [locationType]);

  // Load departments
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const result = await apiClient.getDepartments();
        if (result.success && result.data) {
          setDepartments(result.data.map(d => ({ id: d.id, name: d.departmentName })));
        }
      } catch (err) {
        // Silently fail for non-critical filter data
        console.error('Failed to load departments:', err);
      }
    };
    loadDepartments();
  }, []);

  // Load frequencies
  useEffect(() => {
    const loadFrequencies = async () => {
      try {
        const result = await apiClient.getIndicatorFrequencies();
        if (result.success && result.data) {
          setFrequencies(result.data);
        }
      } catch (err) {
        // Silently fail for non-critical filter data
        console.error('Failed to load frequencies:', err);
      }
    };
    loadFrequencies();
  }, []);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadGroupedData = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // If user has no assignment, don't fetch data
      if (hasNoAssignment) {
        setGroupedData([]);
        setTotalCount(0);
        setIsLoading(false);
        return;
      }
      
      // Determine which filters to apply based on user role and location type
      let districtIdFilter: string | undefined;
      let blockIdFilter: string | undefined;
      
      if (isRegularUser) {
        // For regular users, use their assigned district/block
        if (user.blockId) {
          blockIdFilter = user.blockId;
        } else if (user.districtId) {
          districtIdFilter = user.districtId;
        }
      } else {
        // For SuperAdmin, use selected filters
        if (locationType === "district" && selectedDistrict !== "all") {
          districtIdFilter = selectedDistrict;
        } else if (locationType === "block" && selectedBlock !== "all") {
          blockIdFilter = selectedBlock;
        }
      }
      // For "both", don't apply district or block filter to show all
      
      const result = await apiClient.getAllGroupedIndicatorData(
        currentPage,
        pageSize,
        debouncedSearchQuery || undefined,
        frequencyFilter !== 'all' ? frequencyFilter : undefined,
        undefined, // stateId - not used anymore
        districtIdFilter,
        blockIdFilter,
        departmentFilter !== 'all' ? departmentFilter : undefined,
        programTypeFilter !== 'all' ? programTypeFilter : undefined
      );
      
      if (result.success && result.data) {
        setGroupedData(result.data.items);
        setTotalCount(result.data.totalCount);
      }
    } catch (err: unknown) {
      showToast.error(handleApiError(err, "Failed to load indicator data"));
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, debouncedSearchQuery, frequencyFilter, selectedDistrict, selectedBlock, departmentFilter, programTypeFilter, locationType, isRegularUser, user, hasNoAssignment]);

  useEffect(() => {
    loadGroupedData();
  }, [loadGroupedData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery, frequencyFilter, departmentFilter, programTypeFilter, selectedDistrict, selectedBlock, locationType]);

  const toggleIndicator = (indicatorId: string) => {
    setExpandedIndicators(prev => {
      const newSet = new Set(prev);
      if (newSet.has(indicatorId)) {
        newSet.delete(indicatorId);
      } else {
        newSet.add(indicatorId);
      }
      return newSet;
    });
  };

  const clearAllFilters = () => {
    setSearchQuery("");
    if (!isRegularUser) {
      setLocationType("both");
      setSelectedDistrict("all");
      setSelectedBlock("all");
    }
    setFrequencyFilter("all");
    setDepartmentFilter("all");
    setProgramTypeFilter("all");
  };

  const handleAddIndicatorData = () => {
    if (!user) return;

    // Check if user is SuperAdmin
    if (user.roles.includes("SuperAdmin")) {
      // Open modal for SuperAdmin to select district or block
      setIsAddModalOpen(true);
    } else {
      // For regular User, check their assignment
      if (user.blockId) {
        // User has block assignment - redirect to block indicators
        navigate(`/dashboard/indicator-data/block/${user.blockId}/indicators`);
      } else if (user.districtId) {
        // User has district assignment - redirect to district indicators
        navigate(`/dashboard/indicator-data/district/${user.districtId}/indicators`);
      }
      // If neither district nor block, button should be hidden (handled in render)
    }
  };

  const handleModalSubmit = () => {
    if (selectedLevelType === "district" && selectedDistrictForAdd) {
      navigate(`/dashboard/indicator-data/district/${selectedDistrictForAdd}/indicators`);
      setIsAddModalOpen(false);
    } else if (selectedLevelType === "block" && selectedBlockForAdd) {
      navigate(`/dashboard/indicator-data/block/${selectedBlockForAdd}/indicators`);
      setIsAddModalOpen(false);
    } else {
      showToast.error("Please select a " + (selectedLevelType === "district" ? "district" : "block"));
    }
  };

  const closeAddModal = () => {
    setIsAddModalOpen(false);
    setSelectedLevelType("district");
    setSelectedDistrictForAdd("");
    setSelectedBlockForAdd("");
  };

  // Determine if Add Indicator Data button should be shown
  const showAddButton = user && (user.roles.includes("SuperAdmin") || user.districtId || user.blockId);

  const getFrequencyColor = (frequencyName: string) => {
    const name = frequencyName.toLowerCase();
    if (name.includes("yearly")) return "bg-blue-600 text-white";
    if (name.includes("quarterly")) return "bg-green-600 text-white";
    if (name.includes("monthly")) return "bg-purple-600 text-white";
    if (name.includes("weekly")) return "bg-orange-600 text-white";
    return "bg-gray-400 dark:bg-gray-700 text-gray-900 dark:text-gray-300";
  };

  // Transform grouped data to ExpandableRowData format
  const expandableRows = useMemo<ExpandableRowData<IndicatorValueDto>[]>(() => {
    let filteredData = groupedData;
    
    // Apply location type filtering
    if (locationType === "district") {
      // District Level Only: show only records where DistrictId is not null
      filteredData = filteredData.filter(group => group.districtId != null);
    } else if (locationType === "block") {
      // Block Level Only: show only records where BlockId is not null
      filteredData = filteredData.filter(group => group.blockId != null);
    }
    // For "both", no location filtering is applied
    // Note: Department filter is applied on the backend, no need for client-side filtering
    
    return filteredData.map((group) => {
      const badges = [
        {
          label: group.frequencyName,
          color: getFrequencyColor(group.frequencyName),
        },
      ];
      
      if (group.departmentName) {
        badges.push({
          label: group.departmentName,
          color: "bg-blue-600 text-white",
        });
      }
      
      // Add location badge (district or block)
      if (group.blockName) {
        badges.push({
          label: `📍 ${group.blockName}`,
          color: "bg-emerald-600 text-white",
        });
      } else if (group.districtName) {
        badges.push({
          label: `📍 ${group.districtName}`,
          color: "bg-teal-600 text-white",
        });
      }
      
      return {
        id: `${group.indicatorId}-${group.blockId || group.districtId}`,
        title: group.indicatorName,
        subtitle: undefined,
        badges,
        progress: {
          current: group.filledPeriods,
          total: group.totalPeriods,
        },
        metadata: `${group.values.length} ${group.values.length === 1 ? 'entry' : 'entries'}`,
        detailRows: group.values,
        data: group,
      };
    });
  }, [groupedData, departmentFilter, locationType]);

  // Define detail table columns
  const detailColumns: DetailColumn<IndicatorValueDto>[] = [
    {
      key: "period",
      header: "Period",
      align: "left",
      render: (value) => <span className="font-medium">{value.periodLabel}</span>,
    },
    {
      key: "numerator",
      header: (rowData: AllGroupedIndicatorDataDto) => rowData.numeratorLabel || "Numerator",
      align: "right",
      render: (value) => <span>{value.numeratorValue !== null && value.numeratorValue !== undefined ? value.numeratorValue : '-'}</span>,
    },
    {
      key: "denominator",
      header: (rowData: AllGroupedIndicatorDataDto) => rowData.denominatorLabel || "Denominator",
      align: "right",
      render: (value) => <span>{value.denominatorValue !== null && value.denominatorValue !== undefined ? value.denominatorValue : '-'}</span>,
    },
    {
      key: "value",
      header: "Value",
      align: "right",
      render: (value) => <span className="font-semibold">{value.value.toFixed(2)}</span>,
    },
    {
      key: "enteredBy",
      header: "Entered By",
      align: "left",
      render: (value) => <span className="text-sm">{value.enteredByUserName || 'Unknown'}</span>,
    },
    {
      key: "date",
      header: "Date",
      align: "left",
      render: (value) => <span className="text-sm text-gray-600 dark:text-gray-400">{new Date(value.createdAt).toLocaleDateString()}</span>,
    },
    {
      key: "remarks",
      header: "Remarks",
      align: "left",
      render: (value) => <span className="text-sm text-gray-600 dark:text-gray-400">{value.remarks || '-'}</span>,
    },
  ];

  return (
    <>
      <div className="space-y-6">
        <Card className="shadow-sm">
        <CardHeader className="border-b bg-gray-50/50 dark:bg-gray-800/50">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <BarChart3 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <CardTitle className="text-2xl">All Indicator Data</CardTitle>
                <CardDescription className="mt-1">View and filter all indicator data across states, districts, and blocks</CardDescription>
              </div>
            </div>
            {showAddButton && (
              <Button onClick={handleAddIndicatorData} className="shadow-sm">
                <Plus className="mr-2 h-4 w-4" />
                Add Indicator Data
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Search and Filter Controls */}
          <div className="mb-6 space-y-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Search & Filters</h3>
              {totalCount > 0 && (
                <div className="flex items-center gap-2 px-2.5 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
                  <span>{totalCount}</span>
                  <span>Total Indicators</span>
                </div>
              )}
            </div>

            {/* Search and Filters Row - Different layout for regular users */}
            {isRegularUser ? (
              // Regular User: Show search and filters in a single row (4 columns)
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search indicators..."
                    value={searchQuery}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                    className="pl-9 h-10"
                  />
                </div>
                
                <Select value={frequencyFilter} onValueChange={setFrequencyFilter}>
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="Filter by frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Frequencies</SelectItem>
                    {frequencies.map((freq) => (
                      <SelectItem key={freq.id} value={freq.frequencyName.toLowerCase()}>
                        {freq.frequencyName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="All Departments" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>
                        {dept.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={programTypeFilter} onValueChange={setProgramTypeFilter}>
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="All Programs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Programs</SelectItem>
                    <SelectItem value="Aspirational District Program">Aspirational District Program</SelectItem>
                    <SelectItem value="Aspirational Block Program">Aspirational Block Program</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              // SuperAdmin: Show search on first row, then all 5 filters on second row
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search indicators..."
                    value={searchQuery}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                    className="pl-9 h-10"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <Select value={locationType} onValueChange={(value: "both" | "district" | "block") => setLocationType(value)}>
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="Location Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="both">Both (District & Block)</SelectItem>
                    <SelectItem value="district">District Level Data Only</SelectItem>
                    <SelectItem value="block">Block Level Data Only</SelectItem>
                  </SelectContent>
                </Select>

                <Select 
                  value={selectedDistrict} 
                  onValueChange={setSelectedDistrict}
                  disabled={locationType === "both" || locationType === "block"}
                >
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="Select District" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Districts</SelectItem>
                    {districts.map((district) => (
                      <SelectItem key={district.id} value={district.id}>
                        {district.districtName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select 
                  value={selectedBlock} 
                  onValueChange={setSelectedBlock}
                  disabled={locationType === "both" || locationType === "district"}
                >
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="Select Block" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Blocks</SelectItem>
                    {blocks.map((block) => (
                      <SelectItem key={block.id} value={block.id}>
                        {block.blockName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={frequencyFilter} onValueChange={setFrequencyFilter}>
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="Filter by frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Frequencies</SelectItem>
                    {frequencies.map((freq) => (
                      <SelectItem key={freq.id} value={freq.frequencyName.toLowerCase()}>
                        {freq.frequencyName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="Filter by department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>
                        {dept.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={programTypeFilter} onValueChange={setProgramTypeFilter}>
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="All Programs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Programs</SelectItem>
                    <SelectItem value="Aspirational District Program">Aspirational District Program</SelectItem>
                    <SelectItem value="Aspirational Block Program">Aspirational Block Program</SelectItem>
                  </SelectContent>
                </Select>
                </div>
              </>
            )}

            {/* Active Filters */}
            {(searchQuery || (!isRegularUser && (locationType !== "both" || selectedDistrict !== "all" || selectedBlock !== "all")) || frequencyFilter !== "all" || departmentFilter !== "all" || programTypeFilter !== "all") && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-600 dark:text-gray-400">Active filters:</span>
                <div className="flex flex-wrap gap-2">
                  {searchQuery && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md text-xs">
                      Search: {searchQuery}
                      <button onClick={() => setSearchQuery("")} className="hover:text-blue-900 dark:hover:text-blue-100">×</button>
                    </span>
                  )}
                  {!isRegularUser && locationType !== "both" && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-md text-xs">
                      Location: {locationType === "district" ? "District Level Data Only" : "Block Level Data Only"}
                      <button onClick={() => setLocationType("both")} className="hover:text-purple-900 dark:hover:text-purple-100">×</button>
                    </span>
                  )}
                  {!isRegularUser && selectedDistrict !== "all" && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-md text-xs">
                      District: {districts.find(d => d.id === selectedDistrict)?.districtName}
                      <button onClick={() => setSelectedDistrict("all")} className="hover:text-green-900 dark:hover:text-green-100">×</button>
                    </span>
                  )}
                  {!isRegularUser && selectedBlock !== "all" && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 rounded-md text-xs">
                      Block: {blocks.find(b => b.id === selectedBlock)?.blockName}
                      <button onClick={() => setSelectedBlock("all")} className="hover:text-teal-900 dark:hover:text-teal-100">×</button>
                    </span>
                  )}
                  {frequencyFilter !== "all" && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-md text-xs">
                      Frequency: {frequencyFilter}
                      <button onClick={() => setFrequencyFilter("all")} className="hover:text-orange-900 dark:hover:text-orange-100">×</button>
                    </span>
                  )}
                  {departmentFilter !== "all" && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 rounded-md text-xs">
                      Department: {departments.find(d => d.id === departmentFilter)?.name || departmentFilter}
                      <button onClick={() => setDepartmentFilter("all")} className="hover:text-pink-900 dark:hover:text-pink-100">×</button>
                    </span>
                  )}
                  {programTypeFilter !== "all" && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-md text-xs">
                      Program: {programTypeFilter}
                      <button onClick={() => setProgramTypeFilter("all")} className="hover:text-indigo-900 dark:hover:text-indigo-100">×</button>
                    </span>
                  )}
                  <button
                    onClick={clearAllFilters}
                    className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 underline"
                  >
                    Clear all
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Data Table or Empty State */}
          {expandableRows.length === 0 && !isLoading ? (
            <div className="text-center py-16 px-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
                <BarChart3 className="h-8 w-8 text-gray-400 dark:text-gray-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {hasNoAssignment
                  ? "No assignment found"
                  : (searchQuery || (!isRegularUser && (locationType !== "both" || selectedDistrict !== "all" || selectedBlock !== "all")) || frequencyFilter !== "all" || departmentFilter !== "all")
                  ? "No indicators match your filters" 
                  : "No indicator data found"
                }
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto">
                {hasNoAssignment
                  ? "You need to be assigned to a district or block to view indicator data"
                  : (searchQuery || (!isRegularUser && (locationType !== "both" || selectedDistrict !== "all" || selectedBlock !== "all")) || frequencyFilter !== "all" || departmentFilter !== "all")
                  ? "Try adjusting your search terms or filters to find what you're looking for"
                  : "Start adding indicator data to see it here"
                }
              </p>
              {!hasNoAssignment && (searchQuery || (!isRegularUser && (locationType !== "both" || selectedDistrict !== "all" || selectedBlock !== "all")) || frequencyFilter !== "all" || departmentFilter !== "all") && (
                <Button
                  variant="outline"
                  onClick={clearAllFilters}
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Clear All Filters
                </Button>
              )}
            </div>
          ) : (
            <DataTableExpandable
              rows={expandableRows}
              detailColumns={detailColumns}
              expandedRowIds={expandedIndicators}
              onToggleRow={toggleIndicator}
              emptyMessage="No indicator data available"
              emptyMessageWithFilters="No indicators found"
              showSearch={false}
              showFilter={false}
              loading={isLoading}
              showPagination={true}
              currentPage={currentPage}
              pageSize={pageSize}
              totalCount={totalCount}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size: number) => {
                setPageSize(size);
                setCurrentPage(1);
              }}
              getRowKey={(row) => row.id}
            />
          )}
        </CardContent>
      </Card>
      </div>

      {/* Add Indicator Data Modal (SuperAdmin only) */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9998]">
          <Card className="w-full max-w-md shadow-2xl m-4">
            <CardHeader className="border-b border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/10 rounded-lg">
                    <Plus className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <CardTitle>Add Indicator Data</CardTitle>
                    <CardDescription className="mt-1">
                      Select location level and area
                    </CardDescription>
                  </div>
                </div>
                <button 
                  onClick={closeAddModal} 
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Location Level
                  </label>
                  <Select value={selectedLevelType} onValueChange={(value: "district" | "block") => {
                    setSelectedLevelType(value);
                    setSelectedDistrictForAdd("");
                    setSelectedBlockForAdd("");
                  }}>
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Select level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="district">District Level Data</SelectItem>
                      <SelectItem value="block">Block Level Data</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {selectedLevelType === "district" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Select District
                    </label>
                    <Select value={selectedDistrictForAdd} onValueChange={setSelectedDistrictForAdd}>
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="Choose a district" />
                      </SelectTrigger>
                      <SelectContent>
                        {districts.map((district) => (
                          <SelectItem key={district.id} value={district.id}>
                            {district.districtName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {selectedLevelType === "block" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Select Block
                    </label>
                    <Select value={selectedBlockForAdd} onValueChange={setSelectedBlockForAdd}>
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="Choose a block" />
                      </SelectTrigger>
                      <SelectContent>
                        {blocks.map((block) => (
                          <SelectItem key={block.id} value={block.id}>
                            {block.blockName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700 mt-6">
                <Button type="button" variant="outline" onClick={closeAddModal}>
                  Cancel
                </Button>
                <Button 
                  type="button" 
                  onClick={handleModalSubmit}
                  disabled={
                    (selectedLevelType === "district" && !selectedDistrictForAdd) ||
                    (selectedLevelType === "block" && !selectedBlockForAdd)
                  }
                >
                  Continue
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
